<script>
// "Ajouter un article" form of the demande d'avoir modals (AddNewAvoirModal and AddNewAvoirModal1).
// Emits "add" with the line; the parent calls reset() once the server has saved it.
import { debounce } from "lodash";
import ItemsApi from "@/services/items-api";
import AvoirRequestApi from "@/services/avoirRequest-api";
import QuickOrderItemSelect from "@/components/QuickOrderItemSelect";

export default {
  name: "AvoirLineEntry",
  components: { QuickOrderItemSelect },

  props: {
    showBlNumber: { type: Boolean, default: false },
    showExpirationDate: { type: Boolean, default: false },
    // a scanned code that matches exactly one article selects it
    barcodeScanner: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      item: null,
      options: [],
      searchText: "",
      isSearching: false,
      // false once the server answered without /api/avoir/items: then the catalogue search is used
      isLightLookupAvailable: true,
      quantity: null,
      blNumber: "",
      batchNumber: "",
      batchDate: "",
      expirationDate: "",
      reason: "",
      submitted: false,
      reasons: [
        "Produit endommagé",
        "Erreur de livraison",
        "Produit non commandé",
        "Quantité livrée erronée",
        "Date de péremption courte",
        "Produit périmé",
      ],

      // "Parcourir" : whole catalogue with pagination
      showCatalog: false,
      catalogFilter: "",
      catalogItems: [],
      catalogBusy: false,
      catalogPage: { currentPage: 1, perPage: 15, pageSize: 15, totalRows: 0 },
      catalogFields: [
        { key: "ref", label: "Référence", tdClass: "align-middle text-nowrap" },
        { key: "title", label: "Article", tdClass: "align-middle font-weight-bold" },
        { key: "packageCount", label: "Unités / carton", thClass: "text-right", tdClass: "text-right align-middle" },
        { key: "priceTaxIncl", label: "Prix TTC", thClass: "text-right", tdClass: "text-right align-middle font-weight-bold" },
        { key: "pick", label: "", tdClass: "text-right align-middle", thStyle: "width:110px;" },
      ],
    };
  },

  created() {
    this.debouncedSearch = debounce(this.search, 300);
  },

  computed: {
    itemState() {
      return this.submitted && !this.item ? false : null;
    },
    quantityState() {
      return this.submitted && !(Number(this.quantity) > 0) ? false : null;
    },
    isMSS() {
      return process.env.VUE_APP_CLIENT_NAME === "mss";
    },
    shouldShowBlNumber() {
      return this.showBlNumber;
    },
    shouldShowBatchFields() {
      return !this.isMSS;
    },
    detailColumnClass() {
      const count = (this.shouldShowBatchFields ? 2 : 0) + (this.shouldShowBlNumber ? 1 : 0) + (this.showExpirationDate ? 1 : 0);
      return count >= 4 ? "col-6 col-lg-3" : count === 3 ? "col-6 col-lg-4" : "col-6";
    },
  },

  methods: {
    onSearch(text) {
      this.searchText = text;
      if (text && text.trim()) {
        this.isSearching = true;
        this.$refs.itemSelect && this.$refs.itemSelect.setOpen(true);
        this.debouncedSearch();
      } else {
        this.options = [];
      }
    },

    // no suggestion list before something is typed (vue-select 3.10 has no dropdownShouldOpen)
    onOpen() {
      this.$nextTick(() => {
        if (!this.searchText || !this.searchText.trim()) {
          this.$refs.itemSelect && this.$refs.itemSelect.setOpen(false);
        }
      });
    },

    // /api/avoir/items: catalogue lookup without ERP prices/stock; older servers fall back to /api/items/list
    lookupItems(text) {
      const catalogSearch = () => ItemsApi.findItems(1, 10, [], text,
        null, null, false, null, null, null, null, null, null,
        null, false, false, false, false, false, false, false,
        new Date().getTime()).then(result => (result && result.list) || []);
      if (!this.isLightLookupAvailable) return catalogSearch();
      return AvoirRequestApi.findAvoirItems(text, 10).then(list => {
        if (list) return list;
        this.isLightLookupAvailable = false;
        return catalogSearch();
      });
    },

    search() {
      const text = this.searchText;
      this.lookupItems(text)
        .then(list => {
          if (text !== this.searchText) return;
          this.options = list;
          if (this.barcodeScanner && this.options.length === 1) {
            const found = this.options[0];
            if (found.barcode === text.trim() || found.ref === text.trim()) {
              this.selectItem(found);
              this.$refs.itemSelect && this.$refs.itemSelect.setOpen(false);
            }
          }
        })
        .finally(() => { this.isSearching = false; });
    },

    selectItem(item) {
      this.item = item;
      if (item) {
        this.$nextTick(() => {
          const input = this.$refs.quantity && this.$refs.quantity.$el;
          if (input) input.focus();
        });
      }
    },

    formatExpirationDateInput(value) {
      const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
      let month = digits.slice(0, 2);
      const year = digits.slice(2);

      if (month.length === 2 && Number(month) > 12) {
        month = "12";
      }

      this.expirationDate = year ? month + "/" + year : month;
    },

    submit() {
      this.submitted = true;
      if (!this.item || !(Number(this.quantity) > 0) || this.busy) return;
      this.$emit("add", {
        item: this.item,
        quantityAvoir: Number(this.quantity),
        reason: this.reason,
        batchNumber: this.batchNumber,
        batchDate: this.batchDate,
        expirationDate: this.expirationDate,
        blNumber: this.blNumber,
      });
    },

    // after a saved line; the BL number is kept, the next article often comes from the same BL
    reset() {
      this.item = null;
      this.options = [];
      this.searchText = "";
      this.quantity = null;
      this.batchNumber = "";
      this.batchDate = "";
      this.expirationDate = "";
      this.reason = "";
      this.submitted = false;
      this.focusItem();
    },

    focusItem() {
      this.$nextTick(() => {
        const input = this.$el && this.$el.querySelector(".avoir-entry__item input");
        if (input) input.focus();
      });
    },

    openCatalog() {
      this.catalogFilter = this.searchText || "";
      this.catalogPage.currentPage = 1;
      this.showCatalog = true;
      this.findCatalog();
    },

    findCatalog() {
      this.catalogBusy = true;
      ItemsApi.findItems(this.catalogPage.currentPage, this.catalogPage.perPage, [], this.catalogFilter,
        null, null, false, null, null, null, null, null, null,
        null, false, false, false, false, false, false, false,
        new Date().getTime())
        .then(result => {
          if (!result) return;
          this.catalogItems = result.list || [];
          this.catalogPage.totalRows = result.total;
          this.catalogPage.currentPage = result.page;
          this.catalogPage.pageSize = result.pageSize;
        })
        .finally(() => { this.catalogBusy = false; });
    },

    pickFromCatalog(item) {
      if (!item || !item.id) return;
      this.showCatalog = false;
      this.selectItem(item);
    },
  },
};
</script>

<template>
  <div class="avoir-entry">
    <div class="avoir-entry__head">
      <span class="avoir-entry__title"><i class="fas fa-plus-circle text-primary mr-2"></i>Ajouter un article</span>
      <span class="avoir-entry__required">* champs obligatoires</span>
    </div>

    <!-- 1. article + quantity -->
    <div class="form-row">
      <div class="col-12 col-lg-9 avoir-entry__field">
        <label class="avoir-entry__label">Article *</label>
        <div class="avoir-entry__item" :class="{ 'is-invalid': itemState === false }">
          <div class="avoir-entry__search">
          <i class="avoir-entry__search-icon fas" :class="barcodeScanner ? 'fa-barcode' : 'fa-search'"></i>
          <QuickOrderItemSelect
            ref="itemSelect"
            :value="item"
            :options="options"
            :loading="isSearching"
            :searching-text="isSearching ? 'Recherche…' : ''"
            :placeholder="barcodeScanner ? 'Scannez un code-barres ou saisissez une référence ou une désignation.' : 'Référence ou désignation'"
            class="flex-grow-1"
            @search="onSearch"
            @open="onOpen"
            @input="selectItem"
          >
            <template #option="option">
              <div class="qo-option avoir-option">
                <div class="avoir-option__title">{{ option.title }}</div>
                <div class="avoir-option__meta">
                  <span v-if="option.brand && option.brand.name" class="avoir-option__brand">{{ option.brand.name }}</span>
                  <span v-if="option.ref">Réf {{ option.ref }}</span>
                  <span v-if="option.barcode"><i class="fas fa-barcode mr-1"></i>{{ option.barcode }}</span>
                </div>
              </div>
            </template>
          </QuickOrderItemSelect>
          </div>
          <b-button variant="outline-secondary" class="avoir-entry__browse" title="Parcourir le catalogue" @click="openCatalog">
            <i class="fas fa-list-ul"></i><span class="d-none d-md-inline ml-2">Parcourir</span>
          </b-button>
        </div>
        <div v-if="item" class="avoir-entry__picked">
          <span v-if="item.brand && item.brand.name">{{ item.brand.name }} · </span>Réf {{ item.ref }}<span v-if="item.barcode"> · {{ item.barcode }}</span>
        </div>
        <div v-else-if="itemState === false" class="avoir-entry__error">Choisissez un article.</div>
        <div v-else class="avoir-entry__help">
          <template v-if="barcodeScanner"><i class="fas fa-barcode mr-1"></i>Scannez le code-barres : l'article est sélectionné automatiquement. Ou tapez une référence / désignation et choisissez dans la liste.</template>
          <template v-else>Tapez une référence ou une désignation et choisissez dans la liste.</template>
          <span class="d-none d-md-inline"> « Parcourir » ouvre tout le catalogue.</span>
        </div>
      </div>

      <div class="col-12 col-lg-3 avoir-entry__field">
        <label class="avoir-entry__label" for="avoir-entry-qty">Quantité *</label>
        <b-form-input id="avoir-entry-qty" ref="quantity" type="number" min="1" step="1" inputmode="numeric"
          v-model="quantity" :state="quantityState" placeholder="0" class="avoir-entry__qty"
          @keyup.native.enter="submit" />
        <div v-if="quantityState === false" class="avoir-entry__error">Quantité supérieure à 0.</div>
      </div>
    </div>

    <!-- 2. traceability -->
    <div class="form-row">
      <div v-if="shouldShowBlNumber" class="avoir-entry__field" :class="detailColumnClass">
        <label class="avoir-entry__label" for="avoir-entry-bl">N° BL</label>
        <b-form-input id="avoir-entry-bl" v-model="blNumber" placeholder="Ex. BL-10452" />
      </div>
      <div v-if="shouldShowBatchFields" class="avoir-entry__field" :class="detailColumnClass">
        <label class="avoir-entry__label" for="avoir-entry-lot">N° Lot</label>
        <b-form-input id="avoir-entry-lot" v-model="batchNumber" placeholder="Ex. L2405A" />
      </div>
      <div v-if="shouldShowBatchFields" class="avoir-entry__field" :class="detailColumnClass">
        <label class="avoir-entry__label">Date Lot</label>
        <b-form-datepicker v-model="batchDate" :locale="$t('datPicker.locale')" :label-no-date-selected="'jj/mm/aaaa'"
          :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
          reset-button close-button :label-reset-button="$t('datPicker.label-reset-button')" :label-close-button="$t('datPicker.label-close-button')"
          hide-header />
      </div>
      <div v-if="showExpirationDate" class="avoir-entry__field" :class="detailColumnClass">
        <label class="avoir-entry__label">Date péremption</label>
        <b-form-input id="avoir-entry-expiration-date" v-model="expirationDate" type="text" placeholder="mm/yy" maxlength="5"
          inputmode="numeric" autocomplete="off" @input="formatExpirationDateInput" />
      </div>
    </div>

    <!-- 3. reason + action, on the same baseline -->
    <div class="form-row align-items-end">
      <div class="col-12 col-md avoir-entry__field">
        <label class="avoir-entry__label" for="avoir-entry-reason">Motif de la demande</label>
        <b-form-input id="avoir-entry-reason" v-model="reason" list="avoir-entry-reasons"
          placeholder="Choisir ou saisir un motif" @keyup.native.enter="submit" />
        <datalist id="avoir-entry-reasons">
          <option v-for="r in reasons" :key="r" :value="r" />
        </datalist>
      </div>
      <div class="col-12 col-md-auto avoir-entry__field">
        <b-button variant="primary" class="avoir-entry__add" :disabled="busy" @click="submit">
          <b-spinner v-if="busy" small class="mr-2" /><i v-else class="fas fa-plus mr-2"></i>Ajouter à la demande
        </b-button>
      </div>
    </div>

    <b-modal v-model="showCatalog" title="Parcourir le catalogue" size="xl" centered hide-footer
      title-tag="h5" title-class="text-primary" header-close-variant="danger"
      @shown="$refs.catalogSearch && $refs.catalogSearch.focus()">
      <div class="zoom-container">
        <b-input-group class="mb-3">
          <b-form-input ref="catalogSearch" v-model="catalogFilter" placeholder="Référence, désignation ou marque"
            @keyup.native.enter="catalogPage.currentPage = 1; findCatalog()" />
          <template v-slot:append>
            <b-button variant="primary" @click="catalogPage.currentPage = 1; findCatalog()"><i class="fas fa-search mr-1"></i>Rechercher</b-button>
          </template>
        </b-input-group>

        <b-table :items="catalogItems" :fields="catalogFields" :busy="catalogBusy" small hover responsive
          head-variant="light" show-empty tbody-tr-class="avoir-entry__catalog-row" @row-clicked="pickFromCatalog">
          <template v-slot:cell(pick)="{ item }">
            <b-button v-if="item.id" size="sm" variant="outline-primary" @click.stop="pickFromCatalog(item)">Choisir</b-button>
          </template>
          <template v-slot:table-busy>
            <div class="text-center text-primary my-3"><b-spinner small class="mr-2" />Chargement…</div>
          </template>
          <template v-slot:empty>
            <div class="text-center text-muted my-3">{{ $t("catalog.empty") }}</div>
          </template>
        </b-table>

        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <span class="text-muted font-size-13">{{ catalogPage.totalRows }} articles</span>
          <b-pagination v-model="catalogPage.currentPage" :per-page="catalogPage.pageSize" :total-rows="catalogPage.totalRows"
            :disabled="catalogBusy" class="pagination-rounded mb-0" @input="findCatalog()" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style>
/* the article suggestions are appended to <body>: above the modal (1050), or they open hidden behind it */
body.modal-open > .vs__dropdown-menu {
  z-index: 1070;
}

@media screen {
  .avoir-entry {
    padding: 16px 16px 4px;
    margin-bottom: 20px;
    border: 1px solid #e4e7ec;
    border-radius: 8px;
    background: #fcfcfd;
  }
  .avoir-entry__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 14px;
  }
  .avoir-entry__title {
    font-size: 15px;
    font-weight: 600;
    color: #101828;
  }
  .avoir-entry__required {
    font-size: 12px;
    color: #98a2b3;
  }
  .avoir-entry__field {
    margin-bottom: 14px;
  }
  .avoir-entry__label {
    display: block;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #344054;
  }
  /* every control of the form has the same height, so the rows and the button line up */
  .avoir-entry .form-control,
  .avoir-entry .b-form-datepicker,
  .avoir-entry__add,
  .avoir-entry__browse {
    height: 42px;
    border-radius: 8px;
    font-size: 14px;
  }
  .avoir-entry .b-form-datepicker {
    display: flex;
    align-items: center;
  }
  .avoir-entry .b-form-datepicker label.form-control {
    height: auto;
    border: 0;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 40px;
    color: #344054;
  }
  .avoir-entry__item {
    display: flex;
    gap: 8px;
    align-items: stretch;
  }
  .avoir-entry__item .qo-item-select {
    font-size: 14px;
    min-width: 0;
  }
  .avoir-entry__item.is-invalid .vs__dropdown-toggle {
    border-color: #f46a6a !important;
  }
  .avoir-entry__browse {
    white-space: nowrap;
  }
  .avoir-entry__search {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
  }
  .avoir-entry__search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: #667085;
    font-size: 16px;
    pointer-events: none;
  }
  .avoir-entry__search .vs__dropdown-toggle {
    padding-left: 34px;
  }
  .avoir-option {
    padding: 8px 6px;
    border-bottom: 1px solid #f2f4f7;
  }
  .avoir-option__title {
    font-size: 14px;
    font-weight: 600;
    color: #101828;
    line-height: 1.35;
  }
  .avoir-option__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 2px;
    font-size: 12px;
    color: #667085;
  }
  .avoir-option__brand {
    font-weight: 600;
    color: #344054;
  }
  .avoir-entry__help {
    margin-top: 6px;
    font-size: 12px;
    color: #667085;
  }
  .avoir-entry__qty {
    font-weight: 600;
  }
  .avoir-entry__picked {
    margin-top: 6px;
    font-size: 12px;
    color: #667085;
  }
  .avoir-entry__error {
    margin-top: 6px;
    font-size: 12px;
    color: #f46a6a;
  }
  .avoir-entry__add {
    min-width: 200px;
    font-weight: 600;
    white-space: nowrap;
  }
  .avoir-entry__catalog-row {
    cursor: pointer;
  }
}
@media screen and (max-width: 767.98px) {
  .avoir-entry__add {
    width: 100%;
  }
}
</style>
